import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import LoginPage from './pages/login';
import { AuthProvider, useAuth } from './context/AuthContext';
import Dashboard from './pages/dashboard';
import AdminPanel from './pages/admin-panel';
import Layout from './layout';
import NaoAutorizadoPage from './pages/nao-autorizado';
import { PageAccessTracker } from './tracking/PageAccessTracker';
import { AuthLoadingSkeleton } from './components/skeletons';
import { ThemeProvider, useTheme } from './theme/useTheme';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./App.css";

const ThemedToastContainer: React.FC = () => {
  const { isDarkMode } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      autoClose={4000}
      theme={isDarkMode ? 'dark' : 'light'}
    />
  );
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitialized } = useAuth();
  if (!isInitialized) return <AuthLoadingSkeleton />;
  return user ? <>{children}</> : <Navigate to="/painel-central" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitialized } = useAuth();
  if (!isInitialized) return <AuthLoadingSkeleton />;
  return user ? <Navigate to="/painel-central/dashboard" replace /> : <>{children}</>;
};

const LegacyUserRedirect: React.FC = () => {
  const { email } = useParams();
  const { search } = useLocation();
  const legacyParams = new URLSearchParams(search);
  const targetParams = new URLSearchParams();

  if (email) {
    targetParams.set("editUser", email);
    targetParams.set("loginTasy", legacyParams.get("loginTasy") || "");
  } else {
    targetParams.set("newUser", "true");
  }

  return <Navigate to={`/painel-central/admin-panel?${targetParams.toString()}`} replace />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <Router>
        <PageAccessTracker />
        <ThemedToastContainer />
        <Routes>
          <Route
            path="/painel-central"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          <Route
            path="/painel-central/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/painel-central/admin-panel"
            element={
              <PrivateRoute>
                <AdminPanel />
              </PrivateRoute>
            }
          />

          <Route path="/nao-autorizado" element={<NaoAutorizadoPage />} />

          <Route
            path="/painel-central/add-user"
            element={
              <PrivateRoute>
                <LegacyUserRedirect />
              </PrivateRoute>
            }
          />

          <Route
            path="/painel-central/add-user/:email"
            element={
              <PrivateRoute>
                <LegacyUserRedirect />
              </PrivateRoute>
            }
          />

          <Route path="*" element={<Navigate to="/painel-central" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
